import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString, options = {}) {
  if (!dateString) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };
  
  try {
    return new Date(dateString).toLocaleDateString('en-US', defaultOptions);
  } catch {
    return dateString;
  }
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return formatDate(dateString);
  } catch {
    return dateString;
  }
}

export function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return '';
  
  try {
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age;
  } catch {
    return '';
  }
}

export function getInitials(name) {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getCategoryColor(category) {
  const colors = {
    registration: 'bg-blue-100 text-blue-800',
    consultation: 'bg-sky-100 text-sky-800',
    progress: 'bg-green-100 text-green-800',
    prescription: 'bg-amber-100 text-amber-800',
    lab: 'bg-purple-100 text-purple-800',
    imaging: 'bg-pink-100 text-pink-800',
    discharge: 'bg-rose-100 text-rose-800',
    attachment: 'bg-slate-100 text-slate-800',
  };
  
  return colors[category] || 'bg-slate-100 text-slate-800';
}

export function getCategoryIcon(category) {
  const icons = {
    registration: 'ClipboardList',
    consultation: 'Stethoscope',
    progress: 'FileText',
    prescription: 'Pill',
    lab: 'TestTube',
    imaging: 'Scan',
    discharge: 'LogOut',
    attachment: 'Paperclip',
  };
  
  return icons[category] || 'File';
}

export function getRoleColor(role) {
  const colors = {
    admin: 'bg-red-100 text-red-800',
    physician: 'bg-blue-100 text-blue-800',
    nurse: 'bg-green-100 text-green-800',
    clerk: 'bg-amber-100 text-amber-800',
    readonly: 'bg-slate-100 text-slate-800',
  };
  
  return colors[role] || 'bg-slate-100 text-slate-800';
}

export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
